import { create } from "zustand";
import { emitCartEvent } from "../events/cartEvents";
import {
    createEmptyCartSnapshot,
    CART_STORAGE_KEY,
    readCartSnapshot,
} from "../storage/cartStorage";
import type {
    AddCartItemInput,
    AddToCartOptions,
    CartItem,
    CartOperation,
    CartPersistedState,
    CartStore,
} from "../types/cart.types";
import { applyCartOperations } from "./cartOperations";
import {
    commitCartOperations,
    replaceLegacyCartItems,
    validateAndRepairCartPersistence,
} from "./cartCoordinator";

const readInitialSnapshot = (): CartPersistedState => {
    if (typeof window === "undefined") return createEmptyCartSnapshot();

    try {
        return readCartSnapshot();
    } catch (error) {
        console.error("No se pudo hidratar el carrito persistido.", error);
        return createEmptyCartSnapshot();
    }
};

const initialSnapshot = readInitialSnapshot();

const isSameCartLine = (
    item: CartItem,
    identity: Pick<CartItem, "idProduct" | "idProductVariant">,
): boolean =>
    item.idProduct === identity.idProduct && item.idProductVariant === identity.idProductVariant;

const emitUserAddedItemEvents = (
    previousItems: CartItem[],
    nextItems: CartItem[],
    operations: CartOperation[],
): void => {
    const emittedLines = new Set<string>();

    for (const operation of operations) {
        if (operation.type !== "add" || operation.source !== "USER") continue;

        const lineKey = `${operation.item.idProduct}:${operation.item.idProductVariant}`;
        if (emittedLines.has(lineKey)) continue;
        if (previousItems.some((item) => isSameCartLine(item, operation.item))) continue;

        const addedItem = nextItems.find((item) => isSameCartLine(item, operation.item));
        if (!addedItem) continue;

        emittedLines.add(lineKey);
        emitCartEvent({ type: "ITEM_ADDED", item: addedItem });
    }
};

export const useCartStore = create<CartStore>((set, get) => ({
    items: initialSnapshot.items,
    persistedItems: initialSnapshot.items,
    revision: initialSnapshot.revision,
    isOpen: false,
    isOverrideActive: false,

    hydrateCartItems: async (items) => {
        const result = await replaceLegacyCartItems(Array.isArray(items) ? items : []);
        set({
            items: result.items,
            persistedItems: result.items,
            revision: result.revision,
            isOverrideActive: false,
        });
    },

    beginCartOverride: (items) => {
        set({
            items: Array.isArray(items) ? items : [],
            isOverrideActive: true,
        });
    },

    endCartOverride: () => {
        let latestSnapshot: CartPersistedState;
        try {
            latestSnapshot = readCartSnapshot();
        } catch (error) {
            console.error(
                "No se pudo restaurar el carrito persistido al finalizar el override.",
                error,
            );
            latestSnapshot = {
                items: get().persistedItems,
                revision: get().revision,
                updatedAt: new Date().toISOString(),
            };
        }

        set({
            items: latestSnapshot.items,
            persistedItems: latestSnapshot.items,
            revision: latestSnapshot.revision,
            isOverrideActive: false,
        });
    },

    applyOperations: async (operations) => {
        if (operations.length === 0) return get().items;

        if (get().isOverrideActive) {
            const previousItems = get().items;
            const nextItems = applyCartOperations(previousItems, operations);
            set({ items: nextItems });
            emitUserAddedItemEvents(previousItems, nextItems, operations);
            return nextItems;
        }

        const previousItems = get().persistedItems;
        const result = await commitCartOperations(operations, previousItems);
        set((state) =>
            state.isOverrideActive
                ? {
                      persistedItems: result.items,
                      revision: result.revision,
                  }
                : {
                      items: result.items,
                      persistedItems: result.items,
                      revision: result.revision,
                  },
        );

        emitUserAddedItemEvents(previousItems, result.items, operations);

        return get().items;
    },

    validateAndRepairCart: async () => {
        const snapshot = await validateAndRepairCartPersistence();
        set((state) =>
            state.isOverrideActive
                ? {
                      persistedItems: snapshot.items,
                      revision: snapshot.revision,
                  }
                : {
                      items: snapshot.items,
                      persistedItems: snapshot.items,
                      revision: snapshot.revision,
                  },
        );

        return get().items;
    },

    applyExternalSnapshot: (snapshot) => {
        if (snapshot.revision <= get().revision) return;

        set((state) =>
            state.isOverrideActive
                ? {
                      persistedItems: snapshot.items,
                      revision: snapshot.revision,
                  }
                : {
                      items: snapshot.items,
                      persistedItems: snapshot.items,
                      revision: snapshot.revision,
                  },
        );
    },

    addToCart: async (item: AddCartItemInput, options?: AddToCartOptions) => {
        await get().applyOperations([{ type: "add", item, source: options?.source ?? "USER" }]);
    },

    removeFromCart: async (idProduct, idProductVariant) => {
        await get().applyOperations([{ type: "remove", idProduct, idProductVariant }]);
    },

    updateQuantity: async (idProduct, idProductVariant, quantity) => {
        await get().applyOperations([
            { type: "updateQuantity", idProduct, idProductVariant, quantity },
        ]);
    },

    clearCart: async () => {
        await get().applyOperations([{ type: "clear" }]);
        set({ isOpen: false });
    },

    openCart: () => {
        if (get().isOpen) return;

        set({ isOpen: true });
        void get()
            .validateAndRepairCart()
            .catch((error) => console.error("No se pudo validar el carrito al abrirlo.", error));
    },
    closeCart: () => set({ isOpen: false }),
    toggleCart: () => {
        const willOpen = !get().isOpen;
        set({ isOpen: willOpen });
        if (!willOpen) return;

        void get()
            .validateAndRepairCart()
            .catch((error) => console.error("No se pudo validar el carrito al abrirlo.", error));
    },
}));

const handleStorageChange = (event: StorageEvent): void => {
    if (event.key !== CART_STORAGE_KEY) return;

    try {
        useCartStore.getState().applyExternalSnapshot(readCartSnapshot());
    } catch (error) {
        console.error("No se pudo sincronizar el carrito desde otra pestaña.", error);
    }
};

if (typeof window !== "undefined") {
    const cartWindow = window as typeof window & {
        __ecommerceCartStorageListener?: (event: StorageEvent) => void;
    };

    if (cartWindow.__ecommerceCartStorageListener) {
        window.removeEventListener("storage", cartWindow.__ecommerceCartStorageListener);
    }

    window.addEventListener("storage", handleStorageChange);
    cartWindow.__ecommerceCartStorageListener = handleStorageChange;
}
